import { Component, computed, inject, input, linkedSignal, output } from '@angular/core';
import { Clipboard } from '@angular/cdk/clipboard';
import { form, FormField } from '@angular/forms/signals';
import { IpcData } from '../ipcData';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
	ButtonComponent,
	ButtonToggleComponent,
	ButtonToggleContentDirective,
	OffcanvasComponent,
} from '@agilox/ui';
import { ToastService } from '@agilox/components';
import { FileService } from '@agilox/common';
import { States, UIThemes } from '@agilox/ui-common';
import { EmptyPipe } from '@agilox/pipe/empty';
import { CustomDatePipe } from '@agilox/pipe/custom-date';
import { CustomTimePipe } from '@agilox/pipe/custom-time';
import { CustomNumberPipe } from '@agilox/pipe/custom-number';
import { DetailView } from './detail-view.enum';
import { DetailViewToggles } from './detail-view-toggles.const';
import { tokenizeJson } from './json-tokens.util';

const JSON_INDENT = 2;
const BYTES_PER_KB = 1024;
const JSON_FILE_NAME_PREFIX = 'selftest';
const JSON_FILE_EXTENSION = '.json';
const LINK_BUTTON_TYPE = 'link';
const JSON_FILE_DATE_FORMAT = 'yyyy-MM-dd';

@Component({
	selector: 'selftest-detail',
	templateUrl: './detail.component.html',
	imports: [
		OffcanvasComponent,
		ButtonComponent,
		ButtonToggleComponent,
		ButtonToggleContentDirective,
		FormField,
		TranslatePipe,
		EmptyPipe,
		CustomDatePipe,
		CustomTimePipe,
		CustomNumberPipe,
	],
	providers: [CustomDatePipe],
})
export class DetailComponent {
	private clipboard = inject(Clipboard);
	private customDatePipe = inject(CustomDatePipe);
	private fileService = inject(FileService);
	private toastService = inject(ToastService);
	private translate = inject(TranslateService);

	open = input<boolean>(false);
	element = input<IpcData | undefined>(undefined);

	closeDetails = output<void>();

	isJsonView = computed(() => this.viewField().value() === DetailView.JSON);
	closeButtonTheme = computed(() => (this.isJsonView() ? LINK_BUTTON_TYPE : UIThemes.Primary));
	rawJson = computed(() => JSON.stringify(this.displayedElement()?.raw ?? null, null, JSON_INDENT));
	jsonTokens = computed(() => tokenizeJson(this.rawJson()));
	jsonSizeKb = computed(() => new Blob([this.rawJson()]).size / BYTES_PER_KB);
	jsonFileName = computed(() => {
		const element = this.displayedElement();
		return [
			JSON_FILE_NAME_PREFIX,
			element?.serialnumber,
			this.customDatePipe.transform(element?.latest, undefined, JSON_FILE_DATE_FORMAT),
		]
			.filter(Boolean)
			.join('_')
			.concat(JSON_FILE_EXTENSION);
	});

	displayedElement = linkedSignal<IpcData | undefined, IpcData | undefined>({
		source: this.element,
		computation: (element, previous) => element ?? previous?.value,
	});
	viewField = form(
		linkedSignal<IpcData | false | undefined, DetailView>({
			source: () => this.open() && this.element(),
			computation: (shown, previous) =>
				shown ? DetailView.DETAILS : (previous?.value ?? DetailView.DETAILS),
		})
	);
	viewToggles = DetailViewToggles;
	copyButtonTheme = UIThemes.Secondary;

	doClose(): void {
		this.closeDetails.emit();
	}

	downloadJson(): void {
		if (!this.displayedElement()) {
			return;
		}
		this.fileService.downloadFile(this.jsonFileName(), this.rawJson());
	}

	copyJson(): void {
		if (!this.displayedElement()) {
			return;
		}
		if (this.clipboard.copy(this.rawJson())) {
			this.toastService.show(this.translate.instant('detail.copy_success'), States.Success);
		} else {
			this.toastService.show(this.translate.instant('detail.copy_failed'), States.Warning);
		}
	}
}
